import { useQuery } from "@tanstack/react-query";
import { errorText } from "@/lib/query/client";
import { qk } from "@/lib/query/keys";
import { Boxes, Container, Cpu, FolderOpen, GitBranch, Globe, MemoryStick, TerminalSquare } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { EmptyState, Skeleton } from "@/components/ui/misc";
import { Page, PageHeader } from "@/components/ui/page";
import { api } from "@/lib/api";
import { useCan } from "@/lib/auth";
import { formatBytes, humanizeMs, relativeTime } from "@/lib/format";
import type { AppGroup, AppProcess, AppsStatus } from "@/lib/types";
import { cn } from "@/lib/utils";

/** The worker reads the apps every third host reading, ~30s; polling faster only repeats it. */
const POLL_MS = 10_000;

type Reading = AppsStatus & { observedAt: string; stale: boolean };

/**
 * Every app on the host, and every process each one runs: the docker containers
 * and pm2 processes the worker sees, grouped by the top-level folder of the apps
 * root they run from. Read-only on purpose — it shows what is running, and starting
 * or stopping anything stays with the `apps` CLI.
 */
const DESCRIPTION = "Every app on the host, from docker and pm2.";

export function AppsView({ showTitle = true }: { /** False with the tab layout, whose top bar already names the view. */ showTitle?: boolean }) {
  // Host inventory: the server serves it to admins only.
  const isAdmin = useCan("admin");
  // Polled while visible (the interval pauses in a hidden tab), from the cache on a return.
  const query = useQuery({ queryKey: qk.apps(), queryFn: api.apps, enabled: isAdmin, refetchInterval: POLL_MS });
  const data: Reading | null | undefined = query.data ? query.data.apps : undefined;
  const error = query.error ? errorText(query.error, "Could not load the apps") : null;
  const now = query.dataUpdatedAt || Date.now();

  if (!isAdmin) {
    return (
      <Page>
        <PageHeader title="Apps" showTitle={showTitle} description={DESCRIPTION} />
        <EmptyState icon={<Boxes />} title="Admins only" hint="Apps lists what runs on the host, so only an admin or the owner can see it." />
      </Page>
    );
  }

  if (data === undefined) {
    return (
      <Page className="space-y-3">
        <PageHeader title="Apps" showTitle={showTitle} description={DESCRIPTION} />
        {error ? (
          <EmptyState icon={<Boxes />} title="Could not load the apps" hint={error} />
        ) : (
          <>
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-44 w-full" />
            <Skeleton className="h-28 w-full" />
          </>
        )}
      </Page>
    );
  }
  if (data === null) {
    return (
      <Page>
        <PageHeader title="Apps" showTitle={showTitle} description={DESCRIPTION} />
        <Card className="p-4 text-[13px] text-muted-foreground">
          No reading from the worker yet. It reads docker and pm2 every 30 seconds while it runs on the host; if this
          stays empty, the worker is not running (or runs with <code>STATUS_HOST=false</code>).
        </Card>
      </Page>
    );
  }

  const processes = data.apps.flatMap((app) => app.processes);
  const down = processes.filter((process) => health(process) === "down").length;

  return (
    <Page className="space-y-4">
      <PageHeader title="Apps" showTitle={showTitle} description={DESCRIPTION} className="mb-1" />
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <h2 className="text-[15px] font-semibold tracking-tight">
          {data.apps.length} app{data.apps.length === 1 ? "" : "s"}
          <span className="font-normal text-muted-foreground">
            {" "}
            · {processes.length} process{processes.length === 1 ? "" : "es"}
          </span>
        </h2>
        {down > 0 ? (
          <span className="text-[12.5px] font-medium" style={{ color: "var(--kind-blocked)" }}>
            {down} not running
          </span>
        ) : processes.length > 0 ? (
          <span className="text-[12.5px]" style={{ color: "var(--kind-done)" }}>
            all running
          </span>
        ) : null}
        <span className={cn("ml-auto text-[12px]", data.stale ? "text-[var(--kind-blocked)]" : "text-muted-foreground")}>
          {data.stale ? "Stale — " : ""}
          {data.root ? <span className="font-mono">{data.root}</span> : null} · {relativeTime(data.observedAt, now)}
        </span>
      </div>

      {data.errors.length > 0 ? (
        <Card className="p-3 text-[12.5px]" style={{ color: "var(--kind-review)" }}>
          {data.errors.join(" · ")}
        </Card>
      ) : null}

      {data.apps.length === 0 ? (
        <EmptyState icon={<Boxes />} title="No containers or pm2 processes" hint="The worker saw nothing running on the host." />
      ) : (
        <div className="grid grid-cols-1 gap-3 items-start win-lg:grid-cols-2 [&>*]:min-w-0">
          {data.apps.map((app) => (
            <AppCard key={app.key} app={app} now={now} />
          ))}
        </div>
      )}
      <p className="text-[11.5px] text-muted-foreground">
        Read-only: what docker and pm2 report, grouped by the folder under the apps root each process runs from. Start,
        stop and deploy with the <code>apps</code> CLI.
      </p>
    </Page>
  );
}

type Health = "ok" | "warn" | "down";

function health(process: AppProcess): Health {
  if (process.kind === "pm2") return process.state === "online" ? "ok" : process.state === "launching" ? "warn" : "down";
  if (process.state !== "running") return process.state === "restarting" ? "warn" : "down";
  if (process.health === "unhealthy") return "down";
  return process.health === "starting" ? "warn" : "ok";
}

const HEALTH_COLOR: Record<Health, string> = {
  ok: "var(--kind-done)",
  warn: "var(--kind-review)",
  down: "var(--kind-blocked)",
};

function AppCard({ app, now }: { app: AppGroup; now: number }) {
  const worst = app.processes.reduce<Health>((acc, process) => {
    const h = health(process);
    return h === "down" || acc === "down" ? "down" : h === "warn" || acc === "warn" ? "warn" : "ok";
  }, "ok");
  const up = app.processes.filter((process) => health(process) !== "down").length;
  const cpu = sum(app.processes.map((process) => process.cpuPercent));
  const memory = sum(app.processes.map((process) => process.memoryBytes));

  return (
    <Card className="overflow-hidden">
      <div className="flex items-start gap-2.5 border-b border-border/60 px-4 py-3">
        <span className="mt-1.5 size-2 shrink-0 rounded-full" style={{ backgroundColor: HEALTH_COLOR[worst] }} aria-hidden />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <h3 className="text-[14.5px] font-semibold tracking-tight">{app.name}</h3>
            <span className="text-[12px] tabular-nums" style={{ color: HEALTH_COLOR[worst] }}>
              {up}/{app.processes.length} running
            </span>
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11.5px] text-muted-foreground [&_svg]:size-3 [&_svg]:shrink-0">
            {app.path ? (
              <span className="inline-flex min-w-0 items-center gap-1">
                <FolderOpen />
                <span className="truncate font-mono">{app.path}</span>
              </span>
            ) : null}
            {app.repo ? (
              <span className="inline-flex min-w-0 items-center gap-1">
                <GitBranch />
                <span className="truncate">
                  {app.repo}
                  {app.branch ? `@${app.branch}` : ""}
                </span>
              </span>
            ) : null}
            {app.domains.map((domain) => (
              <a
                key={domain}
                href={`https://${domain}`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex min-w-0 items-center gap-1 text-primary hover:underline"
              >
                <Globe />
                <span className="truncate">{domain}</span>
              </a>
            ))}
          </div>
        </div>
        <div className="shrink-0 text-right text-[11.5px] tabular-nums text-muted-foreground [&_svg]:size-3">
          {cpu !== null ? (
            <p className="inline-flex items-center gap-1">
              <Cpu /> {cpu.toFixed(1)}%
            </p>
          ) : null}
          {memory !== null ? (
            <p className="flex items-center justify-end gap-1">
              <MemoryStick /> {formatBytes(memory)}
            </p>
          ) : null}
        </div>
      </div>
      <ul className="divide-y divide-border/50">
        {app.processes.map((process) => (
          <ProcessRow key={`${process.kind}:${process.name}`} process={process} now={now} />
        ))}
      </ul>
    </Card>
  );
}

function ProcessRow({ process, now }: { process: AppProcess; now: number }) {
  const h = health(process);
  const state = process.health && process.state === "running" ? `${process.state} · ${process.health}` : process.state;
  const detail = [
    process.startedAt ? `up ${humanizeMs(now - Date.parse(process.startedAt))}` : null,
    process.restarts !== null ? `${process.restarts} restart${process.restarts === 1 ? "" : "s"}` : null,
    process.cpuPercent !== null ? `${process.cpuPercent.toFixed(1)}% CPU` : null,
    process.memoryBytes !== null ? formatBytes(process.memoryBytes) : null,
  ].filter(Boolean);
  return (
    <li className="px-4 py-2.5">
      <div className="flex items-center gap-2 text-[13px]">
        <span className="shrink-0 text-muted-foreground [&_svg]:size-3.5">
          {process.kind === "pm2" ? <TerminalSquare /> : <Container />}
        </span>
        <span className="min-w-0 truncate font-medium">{process.name}</span>
        <Badge variant="outline" className="shrink-0">
          {process.kind === "pm2" ? "pm2" : process.service ?? "docker"}
        </Badge>
        <span className="ml-auto shrink-0 text-[12px] font-medium" style={{ color: HEALTH_COLOR[h] }}>
          {state}
        </span>
      </div>
      <p className="mt-0.5 pl-5.5 text-[11.5px] tabular-nums text-muted-foreground">{detail.join(" · ") || "—"}</p>
      {process.image || process.ports.length > 0 ? (
        <p className="mt-0.5 truncate pl-5.5 font-mono text-[10.5px] text-muted-foreground/80" title={process.image ?? undefined}>
          {[process.image, process.ports.join(", ")].filter(Boolean).join("  ·  ")}
        </p>
      ) : null}
    </li>
  );
}

/** The total of the numbers present, or null when none are. */
function sum(values: Array<number | null>): number | null {
  const present = values.filter((value): value is number => value !== null);
  return present.length === 0 ? null : present.reduce((a, b) => a + b, 0);
}
